import { HisDateValidator } from './his-date-validator';
import { Injectable, inject } from '@angular/core';
import { Validators, ValidatorFn } from '@angular/forms';
import { HisValidatorAndMessage } from './his-validator-and-message';
import { HisUiService } from '../service/base/his-ui.service';

export class MessageConfig {
    msgKey: string;
    msgParams?: string[];
}

export class ValidatorConfig {
    type?: string;
    refTo?: string;
    validatorParam?: Date | string;
    msgKey?: string;
    msgParams?: string[];
}

/**
 * Factory für Validator(en) der DataDictionary - Datentypen
 *
 * TODO: Konfiguration vom Server holen oder Globale Konfigurationsschlüssel auflösen können
 */
@Injectable({providedIn: 'root'})
export class HisDdValidatorFactory {
    private hisUiService = inject(HisUiService);


    private msgConfigs: Record<string, MessageConfig> = {
        required: {
            msgKey: 'validation.required'
        },
        maxlength: {
            msgKey: 'de.his.validator.LengthValidator.MAXIMUM',
            msgParams: ['actualLength', 'requiredLength']
        },
        minlength: {
            msgKey: 'de.his.validator.LengthValidator.MINIMUM',
            msgParams: ['actualLength', 'requiredLength']
        },
        min: {
            msgKey: 'de.his.faces.validator.NumberValidator.min',
            msgParams: ['actual', 'min']
        },
        max: {
            msgKey: 'de.his.faces.validator.NumberValidator.max',
            msgParams: ['actual', 'max']
        },
        pattern: {
            msgKey: 'de.his.validator.regex',
            msgParams: ['actualValue', 'requiredPattern']
        },
        minDate: {
            msgKey: 'de.his.validator.PastDateValidator.MINIMUM',
            msgParams: ['refValue', 'actualValue']
        },
        maxDate: {
            msgKey: 'de.his.validator.FutureDateValidator.MAXIMUM',
            msgParams: ['refValue', 'actualValue']
        },
        floatnumber: {
            msgKey: 'validation.number'
        },
        passwordNotEqual: {
            msgKey: 'validation.passwordNotEqual',
        },
    };

    private validatorConfigs: Record<string, ValidatorConfig> = {
        required: {
            type: 'required'
        },
        minLength: {
            type: 'minLength'
        },
        maxLength: {
            type: 'maxLength'
        },
        minValueInteger: {
            type: 'min'
        },
        maxValueInteger: {
            type: 'max'
        },
        maxValueDouble: {
            refTo: 'maxValueInteger'
        },
        minValueDouble: {
            refTo: 'minValueInteger'
        },
        pattern: {
            type: 'pattern'
        },
        minValueDate: {
            type: 'minDate'
        },
        maxValueDate: {
            type: 'maxDate'
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        FUTURE_DATE: {
            refTo: 'minValueDate',
            validatorParam: new Date(), // now()
            msgKey: 'de.his.validator.FutureDateValidator.PAST'
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        PAST_DATE: {
            refTo: 'maxValueDate',
            validatorParam: new Date(), // now()
            msgKey: 'de.his.validator.PastDateValidator.FUTURE'
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        PERSON_NAME: { // TODO: so noch nicht verwendbar. Java-Pattern war nicht JavaScript-kompatibel
            refTo: 'pattern',
            msgKey: 'error.psv.Person.invalidName',
            validatorParam: '.{2,}' // (zu) vereinfachtes Pattern
            // regexKey: 'core.sys.metadata.types.person_name.regexp',
            // regex: '(?=.{2,})(?:[a-zA-ZÖÄÜöäüß’\'´`\u00C0-\u00FF\u0100-\u017F\u0180-\u024F&&[^\u00D7\u00F7]]+\\.?)'
            //  + '(?>(?>[ \\-]?[a-zA-ZÖÄÜöäüß’'´`\u00C0-\u00FF\u0100-\u017F\u0180-\u024F&&[^\u00D7\u00F7]]+\\.?)*)'
            // regex: '[A-Z][a-z]*'
        }
        // ,
        // 'EMAIL': { // Pattern funktioniert so nicht
        //     refTo: 'pattern',
        //     msgKey: 'de.his.validator.email.message',
        //     validatorParam: '[A-Za-z0-9_][a-zA-Z0-9+_\.\-\%]*@[A-Za-z0-9][a-zA-Z0-9\.\-]*\.[A-Za-z]{2,}'
        // }
    };

    private mergedConfigCache: Record<string, ValidatorConfig> = {};

    /**
     * Erzeugt den hier für den configName konfigurierten Validator
     *
     * @param configName Name des Validators ('required', 'min', ...)
     * @param param Validator-Parameter oder null wenn der Validator keinen Parameter hat
     */
    public createValidator( configName: string, param: any ): HisValidatorAndMessage {
        let config = this.getAndMergeConfig( configName );
        return config ? this.createValidatorFromConfig( config, param ) : null;
    }

    /**
     * Erzeugt einen Validator anhand der gegebenen Konfiguration
     */
    private createValidatorFromConfig( config: ValidatorConfig, validatorParam: any ): HisValidatorAndMessage {
        // Wenn in der Config ein Parameter angegeben ist und hier nicht überschrieben, diesen verwenden.
        if ( validatorParam == null && config.validatorParam != null ) {
            validatorParam = config.validatorParam;
        }

        let validatorFn: ValidatorFn = null;
        switch ( config.type ) {
            case 'required': validatorFn = Validators.required; break;
            case 'minLength': validatorFn = Validators.minLength( validatorParam ); break;
            case 'maxLength': validatorFn = Validators.maxLength( validatorParam ); break;
            case 'min': validatorFn = Validators.min( validatorParam ); break;
            case 'max': validatorFn = Validators.max( validatorParam ); break;
            case 'pattern': validatorFn = Validators.pattern( validatorParam ); break;
            case 'minDate': validatorFn = HisDateValidator.minDate( validatorParam ); break;
            case 'maxDate': validatorFn = HisDateValidator.maxDate( validatorParam ); break;
        }

        let validator = new HisValidatorAndMessage( config.type, validatorFn, this.hisUiService );
        validator.messageKey = config.msgKey;
        validator.paramNames = config.msgParams;
        return validator;
    }

    /**
     * Berechnet Fehlermeldungen für Validierungs-Zustände, die nicht über HisValidatorAndMessage entstanden sind.
     */
    public getMessageForError( msgConfigName: string, state: any ): string {
        let msgConfig = this.msgConfigs[msgConfigName];
        if ( msgConfig != null ) {
            return HisValidatorAndMessage.createErrorMessage( state, msgConfig.msgKey, msgConfig.msgParams, this.hisUiService );
        }
        return 'undefined message for error type: ' + msgConfigName;
    }

    /**
     * Holt die Validator-Konfiguration unter dem configName.
     * Wenn dort per 'refTo' ein Verweis auf eine Basiskonfiguration
     * hinterlegt wurde, wird diese zunächst geholt und mit der eigentlichen
     * Konfiguration gemerged.
     * Es wird auf Clonen gearbeitet, um die originalen Konfigurationen nicht zu verändern.
     */
    private getAndMergeConfig( configName: string ): ValidatorConfig {
        // Versuchen, die Konfiguration aus dem Cache zu holen
        let config = this.mergedConfigCache[configName];
        if ( config ) {
            return config;
        }

        config = this.validatorConfigs[configName];
        if ( !config ) {
            console.warn( 'No Validator registered for Type: ' + configName );
            return null;
        }

        // Neu berechnen: MessageConfig-Attribute kommen zuerst, damit sie durch ValidatorConfig-Attribute überschrieben werden können.
        let mergedConfig = new ValidatorConfig();
        let msgConfig: MessageConfig = this.msgConfigs[config.type];
        Object.assign(mergedConfig, msgConfig);

        // Jetzt die Attribute aus der ValidatorConfig hinzufügen, evtl. auch über refTo
        Object.assign(mergedConfig, config);
        if ( config.refTo != null ) {
            let baseConfig = this.getAndMergeConfig( config.refTo );
            if ( baseConfig != null ) {
                Object.assign(mergedConfig, baseConfig);
            }
        }

        // Errechnete Konfiguration in den Cache legen
        this.mergedConfigCache[configName] = mergedConfig;

        return mergedConfig;
    }
}
